import { Dialog } from "@kobalte/core/dialog";
import { subscribeToGlobalEvent } from "@storytell/events";
import type { Asset, Concept, ConceptId, LabelId, ViewId } from "@storytell/types";
import { AnimateChildren } from "@storytell/ui/animation";
import { createEffect, createSignal, on, onCleanup, onMount } from "solid-js";
import { useWire } from "~/wire/WireProvider";
import { type Filters, serverFiltersToUiFilters, useFilterBuilder } from "../filtering/filterBuilder";
import { KnowledgePaneContent } from "./KnowledgePaneContent";
import { useEntitySelection } from "./useEntitySelection";

export const KnowledgePane = () => {
  const wire = useWire();
  const { knowledgeUi } = wire;

  const [search, setSearch] = createSignal("");

  const [filterExpanded, setFilterExpanded] = createSignal(false);

  const assetsSelection = useEntitySelection();
  const conceptsSelection = useEntitySelection();

  const [editView, setEditView] = createSignal(false);
  const [preserveFiltersForNewView, setPreserveFiltersForNewView] = createSignal(false);

  const [selectedConcept, setSelectedConcept] = createSignal<ConceptId | "">("");
  const [assetToHighlight, setAssetToHighlight] = createSignal<string>("");
  const [openingConcept, setOpeningConcept] = createSignal(false);
  const [labelToFilterBy, setLabelToFilterBy] = createSignal<string>("");
  const [applyingLabelFilter, setApplyingLabelFilter] = createSignal(false);

  const hasViewChanges = () => {
    const active = knowledgeUi.activeView();
    if (!active) return false;

    if (active === "all" || active === "new") {
      return filterBuilder.state.filter.groups.length > 0;
    }
    return JSON.stringify(filterBuilder.state.filter) !== JSON.stringify(wire.views.get(active)?.filters);
  };

  onMount(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Cmd/Ctrl + Shift + K
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (knowledgeUi.isOpen()) {
          knowledgeUi.close();
        } else {
          knowledgeUi.open();
        }
      }
    };
    const unsubscribeAddItemsToPromptScope = subscribeToGlobalEvent("add-items-to-prompt-scope", (e) => {
      knowledgeUi.close();
    });
    const unsubscribeOpenConcept = subscribeToGlobalEvent("open-concept-in-pane", (e) => {
      setOpeningConcept(true);
      setSelectedConcept(e.detail.conceptId);
      knowledgeUi.open();
      knowledgeUi.setActiveTab("concepts");
      // Reset flag after the pane has had time to open and render
      setTimeout(() => setOpeningConcept(false), 100);
    });
    const unsubscribeOpenAsset = subscribeToGlobalEvent("open-asset-in-knowledge-pane", (e) => {
      // FORCE switch to assets tab - clear any concept selection immediately
      setSelectedConcept("");
      setOpeningConcept(false);

      // FORCE assets tab and "all" view FIRST - do this multiple times to ensure it sticks
      knowledgeUi.setActiveTab("assets");
      knowledgeUi.setActiveView("all");
      knowledgeUi.open();

      // Force assets tab again after a tiny delay to override any race conditions
      setTimeout(() => {
        knowledgeUi.setActiveTab("assets");
        knowledgeUi.setActiveView("all");
        setSelectedConcept("");
      }, 0);

      // Use setTimeout to ensure the view switch completes before highlighting the asset
      setTimeout(() => {
        // FORCE "all" view again to ensure we're in All Knowledge, not a collection
        knowledgeUi.setActiveView("all");
        // Force assets tab one more time to be absolutely sure
        knowledgeUi.setActiveTab("assets");
        setSelectedConcept("");

        // Now highlight the asset after we're in the "all" view
        setAssetToHighlight(e.detail.assetId);
      }, 50);
    });
    const unsubscribeClearConcept = subscribeToGlobalEvent("clear-selected-concept", () => {
      setSelectedConcept("");
    });
    const unsubscribeOpenLabel = subscribeToGlobalEvent("open-label-in-assets-pane", (e) => {
      // FORCE switch to assets tab - clear any concept selection immediately
      setSelectedConcept("");
      setOpeningConcept(false);
      setLabelToFilterBy(e.detail.labelId);
      setApplyingLabelFilter(true);

      // FORCE assets tab FIRST - do this multiple times to ensure it sticks
      knowledgeUi.setActiveTab("assets");
      knowledgeUi.setActiveView("all");
      knowledgeUi.open();

      // Force assets tab again after a tiny delay to override any race conditions
      setTimeout(() => {
        knowledgeUi.setActiveTab("assets");
        setSelectedConcept("");
      }, 0);

      // Use setTimeout to ensure the view switch completes before adding the filter
      setTimeout(() => {
        // FORCE "all" view again to ensure we're in All Knowledge, not a collection
        knowledgeUi.setActiveView("all");
        // Force assets tab one more time to be absolutely sure
        knowledgeUi.setActiveTab("assets");
        setSelectedConcept("");

        // Wait a bit more to ensure the view switch has fully completed
        setTimeout(() => {
          // Reset all existing filters first, then add only the clicked label filter
          filterBuilder.resetFilter();

          // Create a new filter group with just this label
          filterBuilder.addFilterGroup("and", []);

          // Add the label filter after we're in the "all" view
          filterBuilder.addFilter(
            {
              id: e.detail.labelId as LabelId,
              kind: "label",
              filter: (entity: Asset | Concept) => {
                if (entity.id.startsWith("asset_")) {
                  return wire.rels.assetHasLabel(entity.id as AssetId, e.detail.labelId as LabelId);
                }
                if (entity.id.startsWith("concept_")) {
                  return wire.rels.conceptHasLabel(entity.id as ConceptId, e.detail.labelId as LabelId);
                }
                return true;
              },
            },
            0,
          );

          // Reset flag after filter is applied
          setTimeout(() => {
            setApplyingLabelFilter(false);
            // Final safety check - force assets tab and "all" view one last time
            knowledgeUi.setActiveView("all");
            knowledgeUi.setActiveTab("assets");
            setSelectedConcept("");
          }, 100);
        }, 50);
      }, 50);
    });
    window.addEventListener("keydown", handleKeyDown);
    onCleanup(() => {
      window.removeEventListener("keydown", handleKeyDown);
      unsubscribeAddItemsToPromptScope();
      unsubscribeOpenConcept();
      unsubscribeOpenAsset();
      unsubscribeOpenLabel();
      unsubscribeClearConcept();
    });
  });

  const filterBuilder = useFilterBuilder<Asset | Concept>();

  createEffect(
    on(
      () => knowledgeUi.activeView(),
      (activeView) => {
        setEditView(false);
        // Reset selected concept when switching views (unless we're specifically opening a concept)
        if (!openingConcept()) {
          setSelectedConcept("");
        }

        if (!activeView) {
          return;
        }

        if (activeView === "new") {
          // Only reset filters if we're not preserving them for "Save as View"
          if (!preserveFiltersForNewView()) {
            filterBuilder.resetFilter();
          }
          setPreserveFiltersForNewView(false); // Reset the flag
          return;
        }

        if (activeView === "all") {
          // Don't reset filter if we're applying a label filter
          if (!applyingLabelFilter()) {
            filterBuilder.resetFilter();
          }
          return;
        }
        const view = wire.views.get(activeView);
        if (view) {
          const filters = serverFiltersToUiFilters<Asset | Concept>(wire, view.filters);
          filterBuilder.setState({ filter: filters });
        } else {
          filterBuilder.resetFilter();
          knowledgeUi.setActiveView("all");
        }
      },
    ),
  );

  // Reset selected concept when knowledge pane is closed
  createEffect(() => {
    if (!knowledgeUi.isOpen()) {
      setSelectedConcept("");
      setAssetToHighlight("");
      setOpeningConcept(false);
      setLabelToFilterBy("");
      setApplyingLabelFilter(false);
    }
  });

  return (
    <AnimateChildren
      show={knowledgeUi.isOpen()}
      enter={{ opacity: [0, 1], x: [30, 0] }}
      exit={{ opacity: [1, 0], x: [0, 30] }}
      duration={150}
    >
      {(anim) => (
        <Dialog
          open={anim.isMounted()}
          onOpenChange={(val) => {
            if (!val) {
              knowledgeUi.close();
            }
          }}
        >
          <Dialog.Portal>
            <AnimateChildren
              show={anim.isVisible()}
              enter={{ opacity: [0, 1] }}
              exit={{ opacity: [1, 0] }}
              duration={150}
            >
              {(sAnim) => (
                <Dialog.Overlay
                  ref={sAnim.ref}
                  class="fixed z-50 inset-0 bg-black/30 dark:bg-white/10 animate-fade-in-quick"
                />
              )}
            </AnimateChildren>
            <div ref={anim.ref} class="fixed z-50 inset-0 flex justify-end">
              <Dialog.Content class="py-2 my-2 min-w-[70%] max-h-[calc(100vh-1rem)] mr-2 text-primary-text max-w-xl shadow-2xl bg-panel overflow-y-auto rounded-xl">
                <KnowledgePaneContent
                  selectedConcept={selectedConcept()}
                  setSelectedConcept={setSelectedConcept}
                  hasViewChanges={hasViewChanges()}
                  filterBuilder={filterBuilder}
                  assetsSelection={assetsSelection}
                  conceptsSelection={conceptsSelection}
                  search={search}
                  setSearch={setSearch}
                  filterExpanded={filterExpanded}
                  setFilterExpanded={setFilterExpanded}
                  editView={editView()}
                  setEditView={setEditView}
                  activeView={knowledgeUi.activeView() || "all"}
                  preserveFiltersForNewView={preserveFiltersForNewView}
                  setPreserveFiltersForNewView={setPreserveFiltersForNewView}
                  assetToHighlight={assetToHighlight()}
                  setAssetToHighlight={setAssetToHighlight}
                  labelToFilterBy={labelToFilterBy()}
                  setLabelToFilterBy={setLabelToFilterBy}
                />
              </Dialog.Content>
            </div>
          </Dialog.Portal>
        </Dialog>
      )}
    </AnimateChildren>
  );
};
